import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Sun, Moon, Zap } from 'lucide-react';
import { aiAPI } from '../../../services/api';
import { parseUTCTimestamp } from '../../../utils/dateUtils';

interface SessionEntry {
  id: string;
  source: string;
  title: string;
  created_time: string | null;
  content: string | null;
  tool_calls: number;
}

const SOURCE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  morning_session: { label: 'Morning', icon: Sun, color: 'text-amber-500' },
  evening_session: { label: 'Evening', icon: Moon, color: 'text-indigo-500' },
  manual_session: { label: 'Manual', icon: Zap, color: 'text-blue-500' },
};

function formatTime(dateStr: string): string {
  const date = parseUTCTimestamp(dateStr);
  return date.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function SessionBlock({ session }: { session: SessionEntry }) {
  const [expanded, setExpanded] = useState(false);
  const config = SOURCE_CONFIG[session.source] ?? SOURCE_CONFIG.manual_session;
  const Icon = config.icon;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
        <Icon className={`w-4 h-4 ${config.color} flex-shrink-0`} />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{config.label}</span>
        {session.created_time && (
          <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">{formatTime(session.created_time)}</span>
        )}
        {session.tool_calls > 0 && (
          <span className="text-xs text-gray-400 dark:text-gray-500">{session.tool_calls} tools</span>
        )}
      </button>
      {expanded && (
        <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          {session.content ? (
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{session.content}</p>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic">No output recorded.</p>
          )}
        </div>
      )}
    </div>
  );
}

export const SessionHistorySection: React.FC = () => {
  const [sessions, setSessions] = useState<SessionEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    aiAPI.getRecentSessions(10)
      .then(res => setSessions(res.sessions))
      .catch(() => { /* silently fail */ })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Recent Sessions
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        Output from recent morning, evening, and manual agent sessions.
      </p>

      {loading ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic py-2">Loading...</p>
      ) : sessions.length === 0 ? (
        <div className="px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center italic">
          No sessions yet. Sessions run automatically each morning and evening.
        </div>
      ) : (
        <div className="space-y-1.5">
          {sessions.map(session => (
            <SessionBlock key={session.id} session={session} />
          ))}
        </div>
      )}
    </div>
  );
};
